import { useCallback, useState } from 'react';
import { sumBy } from 'lodash';
import { Card, CardBody, CardHeader, Icon } from '@repo/ui';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { dateUtil, numberUtil } from '@xtnd/web/shared';

import { Trust } from '../../types';

interface TrustsTableProps {
  data: Trust[];
  isLoading: boolean;
  error: Error | null;
}

export const TrustTable = ({ data, isLoading, error }: TrustsTableProps) => {
  const [isExpand, setIsExpand] = useState<boolean>(true);
  const columns: DataTableColumn<Trust>[] = [
    {
      key: 'accountNumber',
      header: 'Account',
      align: 'left',
      renderCell: trust => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {trust.account}
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      align: 'left',
      renderCell: trust => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {dateUtil.formatDate(new Date(trust.date), dateUtil.DateFormat.US)}
        </span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      renderCell: trust => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {numberUtil.formatCurrency(trust.value)}
        </span>
      ),
    },
    {
      key: 'checkNumber',
      header: 'Check #',
      align: 'left',
      renderCell: trust => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {trust.checkNumber}
        </span>
      ),
    },
    {
      key: 'text',
      header: 'Text',
      align: 'left',
      renderCell: trust => (
        <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
          {trust.text}
        </span>
      ),
    },
  ];

  const renderHeader = () => (
    <button
      className="w-full flex items-center justify-between"
      onClick={() => setIsExpand(prev => !prev)}
      aria-label={isExpand ? 'Collapse trust' : 'Expand trust'}
    >
      <div className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Trust</div>
      <span className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light cursor-pointer">
        {isExpand ? <Icon name="HiChevronDown" /> : <Icon name="HiChevronRight" />}
      </span>
    </button>
  );

  const renderFooter = useCallback(() => {
    const totalValue = sumBy(data ?? [], item => item?.value ?? 0);
    return (
      <tr className="border-xtnd-dark bg-xtnd-blue-100">
        <td
          key="totalTrust"
          colSpan={2}
          className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light"
        >
          <span className="font-bold">Total Trust</span>
        </td>
        <td
          key="totalValue"
          colSpan={1}
          className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        >
          <span className="font-bold">{numberUtil.formatCurrency(totalValue)}</span>
        </td>
        <td
          key=""
          colSpan={3}
          className="px-6 py-2 dark:bg-xtnd-dark text-sm dark:text-xtnd-light text-right"
        />
      </tr>
    );
  }, [data]);

  return (
    <Card>
      <CardHeader className={`${!isExpand ? '!border-none !mb-0 !pb-0' : ''}`}>
        {renderHeader()}
      </CardHeader>

      {isExpand && (
        <CardBody>
          <DataTable
            columns={columns}
            data={{
              data: data || [],
              total: data?.length || 0,
            }}
            emptyState={{
              title: 'No trust activity',
              icon: 'FaDatabase',
              description: 'No trust activity data',
            }}
            isLoading={isLoading}
            error={error}
            tableContainerClassName="overflow-y-auto relative"
            tableClassName="min-w-full divide-y divide-gray-200 relative"
            stickyHeader={true}
            loadingRowCount={5}
            loadingColumnCount={7}
            renderFooter={renderFooter}
          />
        </CardBody>
      )}
    </Card>
  );
};
